import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Client-side URL normalizer to reduce duplicate-content signals.
 *
 * - Strips a trailing slash from non-root paths (`/vacatures/` -> `/vacatures`)
 * - Strips known tracking / noise query params (utm_*, fbclid, gclid, msclkid,
 *   mc_cid, mc_eid, ref, ref_source)
 *
 * Uses history.replaceState so it does NOT push a new history entry and does
 * NOT trigger react-router navigation. The visible URL gets cleaned up so the
 * canonical tag and the URL bar agree, which Google uses as a strong signal.
 *
 * Ad landing pages (/vacatures/:slug/campagne) keep their query string: they are
 * noindex, and the ad link values (utm_campaign, utm_content, utm_term, fbclid) are
 * read later, when the vacancy has loaded and when someone applies.
 */
const NOISE_PARAMS = new Set([
  "fbclid",
  "gclid",
  "msclkid",
  "mc_cid",
  "mc_eid",
  "yclid",
  "ref",
  "ref_source",
]);

const NOISE_PREFIXES = ["utm_"];

/** True for the ad landing page of a vacancy, with or without a trailing slash. */
export const isCampaignPath = (pathname: string) => /\/campagne\/?$/.test(pathname);

/** The cleaned URL, or null when nothing changes. */
export function normalizedUrl(pathname: string, search: string, hash = ""): string | null {
  let changed = false;

  // 1. Trailing slash (except root)
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.replace(/\/+$/, "") || "/";
    changed = true;
  }

  // 2. Noise params (never on ad landing pages)
  if (search && !isCampaignPath(pathname)) {
    const params = new URLSearchParams(search);
    let removed = false;
    for (const key of Array.from(params.keys())) {
      if (NOISE_PARAMS.has(key) || NOISE_PREFIXES.some((p) => key.startsWith(p))) {
        params.delete(key);
        removed = true;
      }
    }
    if (removed) {
      const newSearch = params.toString();
      search = newSearch ? `?${newSearch}` : "";
      changed = true;
    }
  }

  return changed ? `${pathname}${search}${hash}` : null;
}

export const useNormalizeUrl = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const { pathname, search, hash } = window.location;
    const next = normalizedUrl(pathname, search, hash);
    if (next) window.history.replaceState(window.history.state, "", next);
  }, [location.pathname, location.search]);
};
